import { useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import useSWR from 'swr';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { Body2 } from '@/components/@IntranetLibrary/Typography';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import {
  buildCostQuotationCard,
  CostQuotationSnapshot,
} from '@/components/Approval/@components/CostQuotation';
import SearchableSelect from '@/components/Approval/@components/SearchableSelect';
import type { ReplaceContentApi } from '@/components/LexicalEditor/plugins/ReplaceContentPlugin';
import { getCostQuotationDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildCostQuotationCandidateLabel,
  buildCostQuotationEditorHtml,
  buildCostQuotationSubject,
  checkCostQuotationCard,
  withCostQuotationCards,
  type CostQuotationCard,
  type CostQuotationDraftSource,
} from '@/utils/approval/costQuotation';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

const CONTROL_HEIGHT = 44;

type Props = {
  isEditMode: boolean;
  editorApi: ReplaceContentApi | null;
};

/**
 * GERP 원가 견적서(121) — 미상신 견적서를 체크할 때마다 발주서 전송·예정원가 생성을 확인하고 카드를 붙임.
 * 체크·해제·삭제 때마다 제목과 본문 에디터를 덮어씀. 편집은 건을 바꿀 수 없고 저장 본문만
 */
const CostQuotationField = ({ isEditMode, editorApi }: Props) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, setValue, getValues } = useFormContext<ApprovalFormValues>();
  const loaded = useWatch({ control, name: 'costQuotation' });
  const styleNoModalRef = useRef<ModalRef>(null);

  const { data: source, isLoading } = useSWR<CostQuotationDraftSource>(
    isEditMode ? null : getCostQuotationDraftSourceUrl(),
    getNestedDataFetcher,
    {
      revalidateOnFocus: false,
      onError: () =>
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') }),
    },
  );
  const candidates = source?.display.candidates ?? [];

  // 불러오는 중인 건 — 셀렉트에는 체크된 채로 보이고, 그 사이 해제하면 응답을 버림
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const pendingRef = useRef(new Set<string>());

  const currentCards = () => getValues('costQuotation')?.cards ?? [];

  const applyCards = (cards: CostQuotationCard[]) => {
    const first = candidates.find((c) => c.aprvId === cards[0]?.aprvId);
    const base = getValues('costQuotation') ?? {
      systemId: first?.sysId ?? candidates[0]?.sysId ?? '',
      // 레거시 콤보는 COST 유형만 조회
      approvalTypeCode: first?.aprvTypcd || candidates[0]?.aprvTypcd || 'COST',
    };
    setValue('costQuotation', withCostQuotationCards(base, cards), {
      shouldDirty: true,
    });
    const styleNos = cards.map((c) => c.styleNo);
    setValue('subject', buildCostQuotationSubject(styleNos, t), {
      shouldDirty: false,
    });
    editorApi?.replace(buildCostQuotationEditorHtml(styleNos, t));
  };

  const setPending = (id: string, on: boolean) => {
    if (on) pendingRef.current.add(id);
    else pendingRef.current.delete(id);
    setPendingIds(Array.from(pendingRef.current));
  };

  // 레거시처럼 한 건씩 확인 — 검사에 걸리면 알림 후 체크 해제
  const addCard = async (aprvId: string) => {
    if (currentCards().some((c) => c.aprvId === aprvId)) {
      snackbar.showError({ message: t('이미 추가 된 품번 입니다.') });
      return;
    }
    if (pendingRef.current.has(aprvId)) return;
    setPending(aprvId, true);
    try {
      const data = await getNestedDataFetcher<CostQuotationDraftSource>(
        getCostQuotationDraftSourceUrl([aprvId]),
      );
      if (!pendingRef.current.has(aprvId)) return;
      const content = data.display.content;
      const card = content?.cards?.find((c) => c.aprvId === aprvId);
      if (!card) {
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
        return;
      }
      const blocked = checkCostQuotationCard(card);
      if (blocked) {
        snackbar.showError({ message: t(blocked) });
        return;
      }
      const candidate = candidates.find((c) => c.aprvId === aprvId);
      applyCards([
        ...currentCards(),
        buildCostQuotationCard(card, content?.partcodes, candidate, t),
      ]);
    } catch {
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      setPending(aprvId, false);
    }
  };

  const removeCards = (ids: string[]) => {
    ids.forEach((id) => setPending(id, false));
    const cards = currentCards();
    const next = cards.filter((c) => !ids.includes(c.aprvId));
    if (next.length !== cards.length) applyCards(next);
  };

  const cards = loaded?.cards ?? [];
  const selectedIds = [...cards.map((c) => c.aprvId), ...pendingIds];

  const handleSelectChange = (next: string[]) => {
    const added = next.filter((id) => !selectedIds.includes(id));
    const removed = selectedIds.filter((id) => !next.includes(id));
    if (removed.length) removeCards(removed);
    added.forEach((id) => addCard(id));
  };

  const body = loaded?.content ? (
    <CostQuotationSnapshot
      content={loaded.content}
      renderCardAction={
        isEditMode
          ? undefined
          : ({ aprvId }) => (
              <BoxButton
                type="button"
                variant="secondary"
                size="small"
                onClick={() => removeCards([aprvId])}
              >
                {t('삭제')}
              </BoxButton>
            )
      }
    />
  ) : null;

  if (isEditMode) return <FieldBlock>{body}</FieldBlock>;

  const styleNos = cards.map((c) => c.styleNo);

  return (
    <FieldBlock>
      <ControlRow>
        <SearchableSelect
          multiple
          value={selectedIds}
          options={candidates.map((c) => ({
            value: c.aprvId,
            label: buildCostQuotationCandidateLabel(c),
          }))}
          placeholder={
            isLoading
              ? t('불러오는 중...')
              : candidates.length
              ? t('원가견적서 선택하세요')
              : t('진행할 내역이 없습니다.')
          }
          noOptionsText={t('진행할 내역이 없습니다.')}
          renderValue={(ids) =>
            ids
              .map((id) => {
                const c = candidates.find((x) => x.aprvId === id);
                return c ? buildCostQuotationCandidateLabel(c) : id;
              })
              .join(', ')
          }
          onChange={handleSelectChange}
          sx={{
            ...approvalOutlineSelectSx,
            flex: 1,
            minWidth: 0,
            height: CONTROL_HEIGHT,
          }}
        />
        {pendingIds.length > 0 && <Spinner theme="black" size="16px" />}
        <BoxButton
          type="button"
          variant="secondary"
          size="medium"
          onClick={() => styleNoModalRef.current?.open()}
        >
          {t('추가 품번 확인')}
        </BoxButton>
      </ControlRow>
      {body}
      <Modal
        ref={styleNoModalRef}
        title={t('품번확인')}
        size="x-small"
        handleClose={() => styleNoModalRef.current?.close()}
      >
        <StyleNoList>
          {styleNos.map((styleNo, i) => (
            <Body2 key={`${styleNo}-${i}`} component="li">
              {styleNo}
            </Body2>
          ))}
        </StyleNoList>
      </Modal>
    </FieldBlock>
  );
};

const ControlRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
  [theme.breakpoints.down('md')]: {
    flexWrap: 'wrap',
    '& > .MuiInputBase-root, & > .MuiFormControl-root': { flexBasis: '100%' },
  },
}));

const StyleNoList = styled('ul')({
  margin: 0,
  padding: 0,
  listStyle: 'none',
  '& > li + li': { marginTop: 4 },
});

export default CostQuotationField;
